import api from './api';
import { OrganizationalRole, SpringPageResponse } from '../types';

export interface CreateOrganizationalRoleDto {
  name: string;
  isAdmin?: boolean;
  displayOrder?: number;
  isActive?: boolean;
}

export interface UpdateOrganizationalRoleDto {
  name?: string;
  isAdmin?: boolean;
  displayOrder?: number;
  isActive?: boolean;
}

/**
 * Jackson strips the `is` prefix from boolean getters (e.g. `isAdmin()` → `admin`).
 * Map the backend shape back to the frontend OrganizationalRole interface.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRole(raw: any): OrganizationalRole {
  return {
    id: raw.id,
    name: raw.name,
    isAdmin: raw.isAdmin ?? raw.admin ?? false,
    displayOrder: raw.displayOrder ?? raw.sortOrder ?? raw.display_order ?? 0,
    isActive: raw.isActive ?? raw.active ?? false,
    createdAt: new Date(raw.createdAt ?? raw.created_at ?? raw.created),
    updatedAt: new Date(raw.updatedAt ?? raw.updated_at ?? raw.updated),
  };
}

export const organizationalRoleService = {
  getAll: async (includeInactive = false): Promise<OrganizationalRole[]> => {
    const response = await api.get<SpringPageResponse<unknown> | unknown[]>('/organizational-roles', {
      params: { includeInactive },
    });
    const data = response.data;
    const items = (data && 'content' in data) ? data.content : Array.isArray(data) ? data : [];
    return items.map(mapRole);
  },

  getOne: async (id: string): Promise<OrganizationalRole> => {
    const response = await api.get(`/organizational-roles/${id}`);
    return mapRole(response.data);
  },

  create: async (data: CreateOrganizationalRoleDto): Promise<OrganizationalRole> => {
    const response = await api.post('/organizational-roles', data);
    return mapRole(response.data);
  },

  update: async (id: string, data: UpdateOrganizationalRoleDto): Promise<OrganizationalRole> => {
    const response = await api.patch(`/organizational-roles/${id}`, data);
    return mapRole(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/organizational-roles/${id}`);
  },

  seed: async (): Promise<void> => {
    await api.post('/organizational-roles/seed');
  },
};
